/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Pagination from "../../Helpers/Pagination";
import dayjs from "dayjs";
import Select from "react-select";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Badge, Col, Row, Input } from "reactstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { getCookie } from "../../Helpers/Utils";

const QuotationList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userRole = getCookie("role") || "admin";

    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);

    const [search, setSearch] = useState("");
    const [status, setStatus] = useState(null);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    const [confirmModal, setConfirmModal] = useState(false);
    const [actionType, setActionType] = useState(null);
    const [selectedId, setSelectedId] = useState(null);

    const limit = 20;

    // ⭐ Simplified status options
    const statusOptions = [
        { value: "", label: "All Status" },
        { value: "draft", label: "Draft" },
        { value: "approved", label: "Approved" }
    ];

    const loadQuotations = async () => {
        dispatch(loadingTrue());
        let url = `/quotation/list.php?page=${page}&limit=${limit}`;

        if (search.trim()) url += `&quotation_number=${search}`;
        if (status?.value) url += `&status=${status.value}`;
        if (fromDate) url += `&date_from=${fromDate.format("YYYY-MM-DD")}`;
        if (toDate) url += `&date_to=${toDate.format("YYYY-MM-DD")}`;

        const res = await Handler({ method: "get", url });

        if (res?.success) {
            setData(res?.data || []);
            setTotal(res?.total || res?.total_count || res?.pagination?.total_rows || 0);
        }
        dispatch(loadingFalse());
    };

    const fetchPdfPreview = (id) => {
        const token = getCookie("token");
        dispatch(loadingTrue());

        const apiUrl = process.env.REACT_APP_API_URL || "https://sfsbill.dizoratbd.com/sfs-backend";
        fetch(`${apiUrl}/quotation/generate_pdf.php`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ quotation_id: id })
        })
            .then(res => {
                if (!res.ok) throw new Error("Failed to generate PDF");
                return res.blob();
            })
            .then(blob => {
                const url = URL.createObjectURL(blob);
                const iframe = document.createElement('iframe');
                iframe.style.display = 'none';
                iframe.src = url;
                document.body.appendChild(iframe);

                iframe.onload = () => {
                    iframe.contentWindow.focus();
                    iframe.contentWindow.print();
                    dispatch(loadingFalse());
                    setTimeout(() => {
                        URL.revokeObjectURL(url);
                        document.body.removeChild(iframe);
                    }, 5000);
                };
            })
            .catch(err => {
                toast.error("Error opening printer");
                dispatch(loadingFalse());
            });
    };

    const openConfirm = (type, id) => {
        setActionType(type);
        setSelectedId(id);
        setConfirmModal(true);
    };

    const closeConfirm = () => {
        setConfirmModal(false);
        setSelectedId(null);
        setActionType(null);
    };

    const handleConfirmAction = async () => {
        let url = "";
        // ⭐ Kept only approve/cancel logic
        if (actionType === "approve") url = "/quotation/approve.php";
        if (actionType === "cancel") url = "/quotation/cancel.php";

        dispatch(loadingTrue());
        const res = await Handler({ method: "post", url, data: { quotation_id: selectedId } });
        dispatch(loadingFalse());

        if (res?.success) {
            closeConfirm();
            toast.success(res.message || `Quotation ${actionType}d successfully`);
            loadQuotations();
        } else {
            toast.error(res.message || "Action Failed");
        }
    };

    useEffect(() => { loadQuotations(); }, [page, status]);

    const handleSearch = () => { setPage(1); loadQuotations(); };

    const resetFilters = () => {
        setSearch(""); setStatus(null); setFromDate(null); setToDate(null); setPage(1);
        setTimeout(() => loadQuotations(), 100);
    };

    const getStatusBadge = (status) => {
        const colors = { draft: "secondary", approved: "success" };
        return <Badge color={colors[status] || "dark"} className="text-uppercase px-3 py-2">{status}</Badge>;
    };

    return (
        <div className="page-card p-4 bg-white shadow-sm rounded-4 border-0">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-bold mb-0 text-dark">Quotation Management</h4>
                {/* <Button color="primary" className="px-4 rounded-pill" onClick={() => navigate('/quotation/create')}>
                    <i className="bi bi-plus-lg me-1"></i> Create Quotation
                </Button> */}
            </div>

            <div className="filter-section mb-4 p-3 bg-light rounded-4">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Row className="g-2 align-items-end">
                        <Col md={3}>
                            <label className="extra-small fw-bold text-muted">QUOTATION NO</label>
                            <Input type="text" placeholder="e.g. SFS-2026-0001" value={search} onChange={e => setSearch(e.target.value)} />
                        </Col>
                        <Col md={2}>
                            <label className="extra-small fw-bold text-muted">STATUS</label>
                            <Select options={statusOptions} value={status} onChange={(val) => setStatus(val)} placeholder="Select..." />
                        </Col>
                        <Col md={2}>
                            <label className="extra-small fw-bold text-muted">FROM DATE</label>
                            <DatePicker value={fromDate} onChange={(val) => setFromDate(val)} slotProps={{ textField: { size: "small", fullWidth: true } }} />
                        </Col>
                        <Col md={2}>
                            <label className="extra-small fw-bold text-muted">TO DATE</label>
                            <DatePicker value={toDate} minDate={fromDate || undefined} onChange={(val) => setToDate(val)} slotProps={{ textField: { size: "small", fullWidth: true } }} />
                        </Col>
                        <Col md={3} className="d-flex gap-2">
                            <Button color="primary" className="flex-grow-1" onClick={handleSearch}>Search</Button>
                            <Button color="link" className="text-decoration-none text-muted" onClick={resetFilters}>Reset</Button>
                        </Col>
                    </Row>
                </LocalizationProvider>
            </div>

            <div className="table-responsive rounded-3">
                <table className="table table-hover align-middle">
                    <thead className="table-dark">
                        <tr>
                            <th className="ps-3">#</th>
                            <th>Quotation No</th>
                            <th>Customer Name</th>
                            <th>Item Reference</th>
                            <th>Vehicles</th>
                            <th>Valid Until</th>
                            <th>Status</th>
                            <th className="text-center pe-3">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.length === 0 ? (
                            <tr><td colSpan="8" className="text-center py-5 text-muted">No records found matching your criteria.</td></tr>
                        ) : data.map((row, i) => (
                            <tr key={row.id}>
                                <td className="ps-3 text-muted">{(page - 1) * limit + i + 1}</td>
                                <td className="fw-bold text-primary">{row.quotation_number}</td>
                                <td style={{ maxWidth: '170px' }}>
                                    <div className="fw-bold text-truncate" title={row.customer_name}>{row.customer_name}</div>
                                    <div className="extra-small text-muted">{row.customer_phone}</div>
                                </td>
                                <td><span className="badge bg-light text-dark border">{row.ref || "-"}</span></td>
                                <td>
                                    {row.items?.length > 0 ? (
                                        <div className="small fw-semibold">
                                            <i className="bi bi-truck me-1"></i>
                                            {row.items.length} {row.items.length > 1 ? "Units" : "Unit"}
                                        </div>
                                    ) : "-"}
                                </td>
                                <td>{dayjs(row.valid_until).format("DD MMM YYYY")}</td>
                                <td>{getStatusBadge(row.status)}</td>
                                <td className="pe-3">
                                    <div className="d-flex justify-content-center gap-2">
                                        <Button size="sm" color="light" className="btn-icon shadow-sm" onClick={() => navigate(`/quotation/view/${row.id}`)} title="View Detail">
                                            <i className="bi bi-eye text-dark"></i>
                                        </Button>

                                        <Button size="sm" color="light" className="btn-icon shadow-sm" onClick={() => fetchPdfPreview(row.id)} title="Direct Print">
                                            <i className="bi bi-printer text-danger"></i>
                                        </Button>

                                        {userRole === 'admin' && row.status === "draft" && (
                                            <>
                                                <Button size="sm" color="light" className="btn-icon shadow-sm" onClick={() => navigate(`/quotation/edit/${row.id}`)} title="Edit">
                                                    <i className="bi bi-pencil text-primary"></i>
                                                </Button>
                                                <Button size="sm" color="light" className="btn-icon shadow-sm border-success-subtle" onClick={() => openConfirm("approve", row.id)} title="Approve Quotation">
                                                    <i className="bi bi-check2-circle text-success fs-6"></i>
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="mt-4 border-top pt-3">
                <Pagination
                    page={page}
                    total={total}
                    limit={limit}
                    onPrev={() => setPage(p => Math.max(1, p - 1))}
                    onNext={() => setPage(p => p + 1)}
                />
            </div>

            {/* Modern Confirmation Modal */}
            <Modal isOpen={confirmModal} toggle={closeConfirm} centered contentClassName="border-0 shadow-lg rounded-4">
                <ModalHeader toggle={closeConfirm} className="bg-light border-0">
                    <span className="fw-bold">Confirm Action</span>
                </ModalHeader>
                <ModalBody className="text-center py-4">
                    <div className="mb-3">
                        <i className={`bi bi-question-circle text-primary`} style={{ fontSize: "4rem" }}></i>
                    </div>
                    <h5 className="fw-bold">Process Quotation?</h5>
                    <p className="text-muted">Are you sure you want to <b>{actionType}</b> this quotation? This action will update the status immediately.</p>
                </ModalBody>
                <ModalFooter className="border-0 pb-4 justify-content-center">
                    <Button color="secondary" className="px-4 rounded-pill" onClick={closeConfirm}>Cancel</Button>
                    <Button color="primary" className="px-4 rounded-pill" onClick={handleConfirmAction}>Confirm {actionType}</Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default QuotationList;